import { PickingInfo } from '@deck.gl/core';
import { IFlightItem } from './flights/types';

// manual & side-effect won't cause map to move to flight (except if encompass is on)
// side-effect is any selection that was not a primary action - for example notes - we select flight to open details to open notes tab.
export type FlightSelectCallbackOptions = {
    source: 'manual' | 'search' | 'fleet-monitor' | 'event-list' | 'context-menu' | 'pinned' | 'side-effect' | 'navigation' | 'fpm' | 'flight-forward';
};
type Callback = (args: { selected: number | null; unselected: number[]; options: FlightSelectCallbackOptions }) => unknown;
type Subscription = { unsubscribe: () => void };

export class Selection {
    private _callbacks: Callback[] = [];
    private _selected: Set<number> = new Set<number>();
    private _version: number = 0;
    private enabled = true;

    constructor() {
        this.onFlightSelected = this.onFlightSelected.bind(this);
    }

    get version(): number {
        return this._version;
    }

    isSelected(recordId: number): boolean {
        return this._selected.has(recordId);
    }

    getSelected(): number[] {
        return Array.from(this._selected);
    }

    hasSelected(): boolean {
        return this._selected.size > 0;
    }

    selectFlight(recordId: number, options: FlightSelectCallbackOptions): boolean {
        if (!this.enabled) {
            return false;
        }
        this.replaceSelection(recordId, options);
        return true;
    }

    deselectFlight(recordId: number, options: FlightSelectCallbackOptions) {
        this.removeFromSelection(recordId, options);
    }

    onFlightSelected(info: PickingInfo, e: any): void {
        if (!this.enabled) {
            return;
        }

        const { object = null } = info;
        if (!object) {
            return;
        }

        const recordId = (object as IFlightItem).id;
        // Multi-select uses Ctrl/Cmd (and Shift for backwards-compat). Shift alone now
        // collides with MapLibre's boxZoom (shift + drag), so Ctrl/Cmd is the primary modifier.
        const { shiftKey, ctrlKey, metaKey } = e.srcEvent.originalEvent;
        const isMultiSelect = shiftKey || ctrlKey || metaKey;
        if (isMultiSelect) {
            if (this.isSelected(recordId)) {
                this.removeFromSelection(recordId, { source: 'manual' });
            } else {
                this.addToSelection(recordId, { source: 'manual' });
            }
        } else {
            this.replaceSelection(recordId, { source: 'manual' });
        }
    }

    on(callback: Callback): Subscription {
        this._callbacks.push(callback);
        return {
            unsubscribe: (() => {
                this.off(callback);
            }).bind(this)
        };
    }

    off(callback: Callback) {
        const idx = this._callbacks.indexOf(callback);
        if (idx !== -1) {
            this._callbacks.splice(idx, 1);
        }
    }

    enable() {
        this.enabled = true;
    }

    disable() {
        this.enabled = false;
    }

    deselectAll() {
        this.fireSelectionChanged(null, this._selected.values().toArray(), { source: 'manual' });
        this._selected.clear();
        this._version++;
    }

    private addToSelection(recordId: number, options: FlightSelectCallbackOptions): void {
        if (this._selected.has(recordId)) {
            return;
        }
        this._selected.add(recordId);
        this._version++;
        this.fireSelectionChanged(recordId, [], options);
    }

    private removeFromSelection(recordId: number, options: FlightSelectCallbackOptions): void {
        if (!this._selected.has(recordId)) {
            return;
        }
        this._selected.delete(recordId);
        this._version++;
        this.fireSelectionChanged(null, [recordId], options);
    }

    private replaceSelection(recordId: number, options: FlightSelectCallbackOptions): void {
        const unselected = [...this._selected.values()];
        this._selected.clear();
        this._selected.add(recordId);
        this._version++;
        this.fireSelectionChanged(recordId, unselected, options);
    }

    private selectSingle(recordId: number): void {
        if (this._selected.has(recordId)) {
            return;
        }

        this._selected.clear();
        this._selected.add(recordId);
        this._version++;
    }

    private fireSelectionChanged(selected: number | null, unselected: number[], options: FlightSelectCallbackOptions) {
        const args = {
            selected,
            unselected: unselected.filter((x) => x !== selected),
            options
        } satisfies Parameters<Callback>[0];
        this._callbacks.forEach((x) => x(args));
    }
}
